import React, {useEffect, useState} from 'react';
import {contractInfoAPI, getClientContractTableAPI} from "../../../services/client";
import styles from './ContractInfoCard.module.css';
import {ContractInfoResponseData} from "../../../types/client";
import MiniTableContainer from "../../common/miniTable/MiniTableContainer";

interface ContractTableData {
    contractId: number;
    startedAt: Date;
    workerAmount: number;
}

//계약 정보 카드
const ContractInfoCard: React.FC = () => {
    const [data, setData] = useState<ContractInfoResponseData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    // Date 객체를 YYYY-MM-DD 문자열로 변환 (로컬 시간대 기준)
//날짜 데이터 형식 변환, 없으면 '-', 있으면 시간 데이터 제외
    const formatDateToString = (date: Date | string): string => {
        if (!date) return '';
        const dateObj = date instanceof Date ? date : new Date(date);

        if (isNaN(dateObj.getTime())) {
            return '-';
        }
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    useEffect(() => {
        const initializeData = async () => {
            try {
                //클라이언트 계약 테이블 조회 API
                const response = await getClientContractTableAPI(0);
                if (response.content.length > 0) {
                    //클라이언트 계약 정보 조회, 테이블 내 첫번째 계약
                    await fetchContractInfo(response.content[0].contractId);
                } else {
                    setLoading(false);
                }
            } catch (error) {
                console.error('Error initializing data:', error);
                setLoading(false);
            }
        };

        initializeData();
    }, []);

    const fetchContractInfo = async (contractId: number) => {
        try {
            setLoading(true);
            //클라이언트 계약 정보 조회 API
            const responseData = await contractInfoAPI(contractId);
            setData(responseData);
        } catch (error) {
            console.error('Error fetching contract info:', error);
        } finally {
            setLoading(false);
        }
    };

    //컬럼
    const tableColumns = [
        {
            key: 'startedAt',
            header: '계약일',
            render: (row: ContractTableData) => {
                const date = new Date(row.startedAt);
                return formatDateToString(date);
            }
        },
        {
            key: 'workerAmount',
            header: '작업자 수',
            render: (row: ContractTableData) => `${row.workerAmount}명`
        }
    ];

    //테이블 탭 버튼
    const tableTabs = [
        {
            id: 'contracts',
            label: '계약 목록',
            fetchData: async (page: number) => {
                return await getClientContractTableAPI(page - 1);
            },
            columns: tableColumns
        }
    ];

    //테이블 로우 선택 시
    const handleRowClick = (row: ContractTableData) => {
        //계약 조회 데이터 변경
        fetchContractInfo(row.contractId);
    };

    //요일
    const formatDate = (date: Date, day: number) => {
        const dayOfWeek = ['월', '화', '수', '목', '금', '토', '일'][day - 1];
        return `${date}(${dayOfWeek})`;
    };

    if (loading) {
        return (
            <div className={styles.container}>
                <div className={styles.loading}>로딩 중...</div>
            </div>
        );
    }

    //계약하지 않은 클라이언트인 경우
    if (!data) {
        return (
            <div className={styles.container}>
                <div className={styles.header}>
                    <h1 className={styles.title}>Subscription Information</h1>
                    <p className={styles.subtitle}>코드비전 데이터 서비스 가입 정보</p>
                </div>
                <div className={styles.alertContainer}>
                    <h3>계약 생성 전입니다.</h3>
                </div>
            </div>
        );
    }

    //그래프 크기
    const radius = 120;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference * (1 - data.percentage / 100) / 2;

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h2 className={styles.title}>Subscription Information</h2>
                <p className={styles.subtitle}>코드비전 데이터 서비스 가입 정보</p>
            </div>

            <div className={styles.content}>
                <div className={styles.tableContainer}>
                    {/*계약 미니 테이블*/}
                    <MiniTableContainer
                        tabs={tableTabs}
                        size="small"
                        itemsPerPage={3}
                        onRowClick={handleRowClick}
                    />
                </div>

                <div className={styles.progressContainer}>
                    {/*계약 정보*/}
                    <div className={styles.infoCard}>
                        <div className={styles.infoRow}>
                            {/*계약일*/}
                            <span className={styles.infoLabel}>서비스 가입</span>
                            <span className={styles.infoValue}>{formatDate(data.registerDate, data.registerDay)}</span>
                        </div>
                        {/*계약 기간*/}
                        <div className={styles.infoRow}>
                            <span className={styles.infoLabel}>구독 기간</span>
                            <span className={styles.infoValue}>{data.duration}개월</span>
                        </div>
                        {/*계약 종료일*/}
                        <div className={styles.infoRow}>
                            <span className={styles.infoLabel}>서비스 종료</span>
                            <span className={styles.infoValue}>{formatDate(data.expireAt, data.expireDay)}</span>
                        </div>
                    </div>
                    {/*계약 만료까지의 진행율 원형 그래프*/}
                    <svg className={styles.progressRing}>
                        <path
                            className={styles.progressBg}
                            d="M 30 140 A 120 120 0 0 1 270 140"
                            fill="none"
                        />
                        <path
                            className={styles.progress}
                            d="M 30 140 A 120 120 0 0 1 270 140"
                            fill="none"
                            strokeDasharray={circumference / 2}
                            strokeDashoffset={strokeDashoffset}
                        />
                        <text x="150" y="95" className={styles.progressText}>
                            <tspan className={styles.until}>서비스 종료일까지</tspan>
                            <tspan x="150" y="130" className={styles.daysLeft}>
                                {data.lastDuration}일
                            </tspan>
                        </text>
                        <text x="30" y="160" className={styles.percentageText}>0%</text>
                        <text x="270" y="160" className={styles.percentageText} textAnchor="end">100%</text>
                    </svg>
                </div>
            </div>
        </div>
    );
}

export default ContractInfoCard;